// cspell: ignore oidc
import { Json } from '@eagle/common';
import { Account, AccountType, UpdateAccountRequest } from '@eagle/core-data-types';
import { Card, CardContent, Stack, styled, TextField, Typography } from '@ethings/ui-toolkit';
import { useTheme } from '@mui/material';
import { isEqual } from 'lodash';
import { useSnackbar } from 'notistack';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { LabelsEditor } from '../../components/labels-editor';
import { useApiErrorHandler, usePromise } from '../../hooks';
import { CacheDataTypes } from '../../types';
import { loadConfig } from '../../util';
import { FetchOneOfAll } from '../fetch';
import { convertPropertiesToObjectFormat } from '../format';
import { LoadingButton } from '../loading-button';
import { LocaleSelect } from '../locale-select';
import { PropertiesElement } from '../properties-element';
import { ResponsiveText } from '../responsive-text';
import { TagsEditor } from '../tags-editor';
import { TextEditor } from '../text-editor';
import { TimeZoneSelect } from '../time-zone-select';
import { AccountDetailPageType, AccountDetailProps } from './account-detail.types';

const StyledCard = styled(Card)({
  height: 'min-content',
});

export const AccountDetailCard: FC<AccountDetailProps> = ({ account, disabled, pageType, ...props }) => {
  const { t } = useTranslation(['common']);
  const { axios, restClient } = useAuthenticated();
  const { handleUpdateError } = useApiErrorHandler();
  const { enqueueSnackbar } = useSnackbar();
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [accountState, setAccountState] = useState<Account>(account);
  const [originalAccount, setOriginalAccount] = useState<Account>(account);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({ email: false, phone: false });
  const [hasLabelErrors, setHasLabelErrors] = useState(false);
  const theme = useTheme();

  const getAccountDetailsUpdatableProps = (accountData: Account): Omit<UpdateAccountRequest, 'config' | 'mediaStorageInfo' | 'oidcGroupSync'> => ({
    display: accountData.display,
    labels: accountData.labels ?? {},
    localization: {
      locale: accountData.localization?.locale,
      timezone: accountData.localization?.timezone,
    },
    policy: accountData.policy,
    properties: convertPropertiesToObjectFormat(accountData.properties),
    supportAccountId: accountData.supportAccountId,
    metricGroup: accountData.metricGroup ?? null,
    tags: accountData.tags ?? [],
  });

  const hasChanges = useMemo(() => {
    return !isEqual(getAccountDetailsUpdatableProps(accountState), getAccountDetailsUpdatableProps(originalAccount));
  }, [accountState, originalAccount]);

  const isFieldsValid = useMemo(() => {
    return !Object.values(fieldErrors).some(Boolean);
  }, [fieldErrors]);

  const handleValidation = (field: string, valid: boolean): void => {
    setFieldErrors((prev) => ({ ...prev, [field]: !valid }));
  };

  const handlePropertyChange = (property: string) => (value: Json): Promise<void> => {
    setAccountState({ ...accountState, [property]: value });
    return Promise.resolve();
  };

  const updateAccountDetails = async (): Promise<void> => {
    try {
      setSaveInProgress(true);
      const updatedAccount = await restClient.account.partialUpdate(account._id, getAccountDetailsUpdatableProps(accountState));
      setAccountState(updatedAccount);
      setOriginalAccount(updatedAccount);
      enqueueSnackbar(t('common:common.hint.update-success', { entity: updatedAccount.display }), { variant: 'success' });
    } catch (err) {
      handleUpdateError(err, CacheDataTypes.ACCOUNT, accountState.display);
    } finally {
      setSaveInProgress(false);
    }
  };

  const [domain] = usePromise(() => loadConfig(axios, account.homeDomain), [account.homeDomain, axios]);

  const disableSave = disabled || saveInProgress || !hasChanges || !isFieldsValid || hasLabelErrors;

  const labelHeadingText = t('common:terms.label');
  const localeHeadingText = t('common:common.labels.locale-settings');
  const domainHeadingText = t('common:page.accounts-list.home-domain.labels');
  const noProductText = t('common:page.accounts-list.no-product-name.labels');

  return (
    <StyledCard data-testid={props['data-testid']}>
      <CardContent>
        <Stack spacing={2}>
          <TextField
            disabled
            data-testid="id-input"
            label={pageType === AccountDetailPageType.MY_ACCOUNT ? t('common:common.labels.id') : t('common:common.labels.account-id')}
            size="small"
            value={account._id}
          />
          <TextEditor
            disabled={disabled || saveInProgress}
            data-testid="display-input"
            label={t('common:common.labels.account-name')}
            onKeyUp={handlePropertyChange('display')}
            size="small"
            value={account.display}
          />
          <TagsEditor
            disabled={disabled || saveInProgress}
            data-testid="tags-editor"
            label={t('common:terms.tag')}
            onChange={handlePropertyChange('tags')}
            placeholder={t('common:common.hint.tag')}
            size="small"
            tags={account.tags}
          />
          {account.properties && account.accountTypeId
            && <FetchOneOfAll
              dataType={CacheDataTypes.ACCOUNT_TYPE}
              id={account.accountTypeId}
              renderFactory={(accountType: AccountType) => (
                <PropertiesElement
                  disabled={disabled || saveInProgress}
                  onChange={handlePropertyChange('properties')}
                  handleValidation={handleValidation}
                  properties={account.properties ?? {}}
                  propertyDefinitions={accountType.properties}
                  size="small"
                />
              )}
            />
          }
          <ResponsiveText
            variant="h6"
            responsiveOptions={{
              minFontSize: '1rem',
              maxFontSize: '1.25rem',
              textLengthThreshold: 25,
            }}
          >
            {labelHeadingText}
          </ResponsiveText>
          <LabelsEditor
            data-testid="labels-editor"
            disabled={disabled || saveInProgress}
            labels={account.labels}
            onChange={handlePropertyChange('labels')}
            onValidationChange={setHasLabelErrors}
            placeholder={t('common:common.hint.label')}
            size="small"
            textfieldLabel={t('common:terms.label')}
          />
          <ResponsiveText
            variant="h6"
            responsiveOptions={{
              minFontSize: '1rem',
              maxFontSize: '1.25rem',
              textLengthThreshold: 25,
            }}
          >
            {localeHeadingText}
          </ResponsiveText>
          <LocaleSelect
            data-testid="locale-select"
            fieldDisabled={disabled || saveInProgress}
            selectedLocale={accountState.localization?.locale ?? ''}
            setSelectedLocale={(value) =>
              handlePropertyChange('localization')({
                ...accountState.localization,
                locale: value !== '' ? value : null,
              })
            }
          />
          <TimeZoneSelect
            data-testid="timezone-select"
            fieldDisabled={disabled || saveInProgress}
            selectedTimeZone={accountState.localization?.timezone ?? ''}
            setSelectedTimeZone={(value) =>
              handlePropertyChange('localization')({
                ...account.localization,
                timezone: value !== '' ? value : null,
              })
            }
          />
          <ResponsiveText
            variant="h6"
            responsiveOptions={{
              minFontSize: '1rem',
              maxFontSize: '1.25rem',
              textLengthThreshold: 25,
            }}
          >
            {domainHeadingText}
          </ResponsiveText>
          <Stack>
            <Typography>{account.homeDomain}</Typography>
            {domain?.productName
              ? <Typography color={theme.palette.text.secondary}>{domain.productName}</Typography>
              : <ResponsiveText
                color={theme.palette.text.secondary}
                variant="body2"
                responsiveOptions={{
                  minFontSize: '0.75rem',
                  maxFontSize: '0.875rem',
                  textLengthThreshold: 30,
                }}
                style={{ fontStyle: 'italic' }}
              >
                {noProductText}
              </ResponsiveText>
            }
          </Stack>
          <LoadingButton
            data-testid="save-button"
            disabled={disableSave}
            loadingCaption={t('common:common.hint.saving')}
            sx={{ alignSelf: 'end', minWidth: 100 }}
            task={updateAccountDetails}
          >
            {t('common:common.action.save')}
          </LoadingButton>
        </Stack>
      </CardContent>
    </StyledCard>
  );
};
